import { createFileRoute } from '@tanstack/react-router'

const DispersalPage = () => {
  return (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <h4 className="mb-2 text-xl font-bold text-gray-800">Seed Dispersal</h4>

      <h4 className="mb-2 text-lg font-bold text-gray-800">
        Dispersal categories used and further information concerning the
        dispersal dataset:
      </h4>

      <p className="mb-2">
        Numerous systems for classifying the methods by which seeds are
        dispersed exist. The classification that we have adopted has been chosen
        to provide clarity and maximal information content, whilst remaining
        fully compatible with the major classification systems. Frequently, not
        all seeds of a given taxon are dispersed in exactly the same way. As
        examples, many seeds that have obvious adaptations for wind dispersal
        may also be transported by water, whilst some animal dispersed fruits
        also fall directly beneath the parent plant and are not further
        dispersed. To counter concerns such as the above we have classified
        species by their principal dispersal agent (PDA); i.e. the mechanism
        that accounts for the majority of seed dispersal. Six main classes of
        PDA are recognised, as detailed below. Names in brackets refer to other
        terms applied to these groups by different authors.
      </p>

      <ul className="ml-6 mb-2 list-disc">
        <li>Animal (zoochory)</li>
        <li>Wind (anemochory)</li>
        <li>Water (hydrochory)</li>
        <li>
          Methods originating from the parent plant or diaspore (autochory)
        </li>
        <li>Unassisted (Barochory)</li>
        <li>Dispersal prevented (atelochory, antitelochory)</li>
      </ul>

      <p className="mb-2">
        Where no one dispersal agent predominates, or two or more methods are
        mentioned in a reference, but data are insufficient to decide which is
        the most important, various combinations of the above six classes are
        used (e.g. PDA=wind+water). Although not relating to seeds, an
        additional class 'vegetative growth' has also been included within the
        PDA field. Plants in this category primarily expand ('disperse') via
        vegetative growth as opposed to by seed. Each PDA is further divided
        into several descriptive sub-groups, and (where available) this
        information is also provided. In addition, dispersal of many seeds
        occurs in a multi-step fashion. For example, seeds of numerous species
        that are predominately animal dispersed initially fall beneath the
        parent plant (unassisted), before being dispersed further by animals.
        This is not always clear from the available data, but where authors have
        indicated that a a multi-step process occurs, it is indicated in the
        notes field. This field also contains information concerning the
        diaspore itself, including features that facilitate dispersal (e.g.
        wings, fleshy fruit etc).
      </p>

      <p className="mb-2">
        Two care points are necessary when interpreting the dispersal data.
        Firstly, different authors have determined dispersal agents in different
        ways, with techniques ranging from direct or experimental observation to
        assumptions based upon diaspore morphology and even anecdotal evidence.
        To allow more confident interpretation of the data, the method by which
        the author(s) of the source reference determined the dispersal agent is
        stated. Secondly, the principal method by which a species is dispersed
        may vary according to the location in which it is growing. For example,
        dispersal by water may predominate in individual plants of a species
        that is usually wind dispersed if they are growing alongside a water
        course. For more complete information concerning how widely applicable
        individual data are likely to be, readers are referred back to the
        source references.
      </p>

      <p className="mb-2">
        A read-across showing selected examples of how categories commonly used
        in other classification schemes (in addition to those already mentioned
        above) will appear in this database is given below. In some cases
        slightly different combinations are also possible, but are broadly
        similar to those given.
      </p>

      <ul className="ml-6 mb-2 list-disc">
        <li>
          Ballistic dispersal — methods originating from parent plant or
          diaspore; explosive mechanism
        </li>
        <li>
          Barochory — unassisted; seeds drop to the ground close to or beneath
          the parent plant
        </li>
        <li>
          Chamaechory — wind; diaspore is rolled along ground surface by wind
        </li>
        <li>Dyszoochory — animal; diaspore is eaten intentionally</li>
        <li>
          Endozoochory — animal; diaspore is eaten intentionally/diaspore is
          eaten accidentally
        </li>
        <li>
          Epizoochory/adhesive dispersal - animal; diaspore is carried
          accidentally
        </li>
        <li>
          Geocarpy — dispersal prevented; seeds are produced below ground level
        </li>
        <li>Meteoranemochory — wind; diaspore is blown by wind</li>
        <li>Myrmecochory — animal; diaspore is carried intentionally; ants</li>
        <li>
          Nautohydrochory — water; e.g. floating/submerged in fresh/saltwater
          currents
        </li>
        <li>
          Ombrohydrochory — water; diaspore is propelled by action of rain on
          plant structure/wetting by rain or dew
        </li>
        <li>Synzoochory — animal; diaspore is carried intentionally</li>
        <li>Xerochasy/hygrochasy — water; wetting by rain or dew</li>
      </ul>
    </div>
  )
}

export const Route = createFileRoute('/definitions/dispersal')({
  component: DispersalPage,
})
